/**
 * billingVmBuilder — monta o BillingVm (@version 3) a partir dos dados crus da API.
 *
 * Função PURA, sem React: é o que permite testar cada derivação (quanto dura, economia, ritmo,
 * PIX pendente, lote vencendo) contra os casos que discriminam bug — catálogo vazio, um pacote
 * só, conta sem ritmo, compra pendente sem produto — sem levantar a página.
 * Mesmo padrão de pages/dashboard/dashboardVmBuilder.js.
 *
 * A View nunca faz conta: tudo que ela exibe sai pronto daqui.
 */
import { formatDate, formatRelativeDistance } from '../../utils/formatters';

export const METER = 'whatsapp_messages';
export const WINDOW_DAYS = 30;
export const EXPIRING_SOON_DAYS = 30;

const NUM = new Intl.NumberFormat('pt-BR');
export const fmtBRL = (cents) => `R$ ${((cents ?? 0) / 100).toFixed(2).replace('.', ',')}`;
export const fmtInt = (n) => NUM.format(Number(n ?? 0));
// Data via formatters (kernel de fuso do Admin) — a regra de lint proíbe toLocaleDateString direto.
const fmtDay = (iso) => (iso ? formatDate(iso, 'dd/MM/yyyy') : '—');
const fmtDayShort = (isoDate) => { const [, m, d] = String(isoDate).split('-'); return `${d}/${m}`; };
const plural = (n, s, p) => (n === 1 ? s : p);

/** "dura cerca de 2 semanas / 3 meses / 1 ano" a partir de dias. "" quando não há ritmo. */
export function durationLabel(days) {
  if (!Number.isFinite(days) || days <= 0) return '';
  if (days < 10) { const d = Math.max(1, Math.round(days)); return `dura cerca de ${d} ${plural(d, 'dia', 'dias')}`; }
  if (days < 45) { const w = Math.max(1, Math.round(days / 7)); return `dura cerca de ${w} ${plural(w, 'semana', 'semanas')}`; }
  if (days < 330) { const m = Math.max(1, Math.round(days / 30)); return `dura cerca de ${m} ${plural(m, 'mês', 'meses')}`; }
  const y = Math.max(1, Math.round(days / 365)); return `dura cerca de ${y} ${plural(y, 'ano', 'anos')}`;
}

/**
 * @param {object} input
 * @param {number}  input.balance            saldo (useMeter)
 * @param {boolean} input.low                abaixo do limiar (useMeter)
 * @param {number}  input.lost               perdas desde o último crédito (useMeter.lost)
 * @param {boolean} input.meterLoading
 * @param {Array}   input.products           GET /v1/billing/products?meter=… (id, creditQty, priceCents, validityDays)
 * @param {boolean} input.productsLoading
 * @param {object|null} input.statement      GET /v1/me/whatsapp/credits/statement (entries, purchases, daily, windowDays)
 * @param {string|null} input.errorMessage
 * @param {string|null} input.ref            querystring ref= (atribuição; presença = veio de um aviso)
 * @param {boolean} input.integrationEnabled user_whatsapp_settings.enabled
 * @param {number}  [input.now]              Date.now() injetável para teste
 */
export function montarVm({
  balance, low, lost, meterLoading,
  products, productsLoading, statement, errorMessage,
  ref, integrationEnabled, now = Date.now(),
}) {
  const prods = Array.isArray(products) ? products : [];
  const saldo = Number(balance ?? 0);
  const perdas = Number(lost ?? 0);

  const daily = statement?.daily ?? [];
  const totalConsumed = daily.reduce((acc, d) => acc + (d.consumed ?? 0), 0);
  const windowDays = statement?.windowDays ?? WINDOW_DAYS;
  const perDay = daily.length ? totalConsumed / windowDays : 0;
  const hasRhythm = perDay > 0;

  const level = saldo <= 0 ? 'zero' : (low ? 'low' : 'ok');

  const runwayDays = hasRhythm && saldo > 0 ? Math.max(1, Math.round(saldo / perDay)) : 0;
  const runwayLabel = !hasRhythm ? ''
    : saldo <= 0 ? 'seu saldo já acabou'
    : `seu saldo acaba em cerca de ${runwayDays} ${plural(runwayDays, 'dia', 'dias')}`;

  const perMsgOf = (p) => (p.creditQty ? p.priceCents / p.creditQty : null);
  const worstPerMsg = prods.reduce((m, p) => { const v = perMsgOf(p); return v != null && v > m ? v : m; }, 0);

  const packages = prods.map((p, i) => {
    const qty = Number(p.creditQty ?? 0);
    const perMsg = perMsgOf(p);
    const saving = perMsg != null && worstPerMsg > 0 && perMsg < worstPerMsg
      ? Math.round((1 - perMsg / worstPerMsg) * 100) : 0;
    return {
      id: String(p.id),
      qty,
      qtyLabel: `${fmtInt(qty)} mensagens`,
      priceLabel: fmtBRL(p.priceCents),
      perMsgLabel: perMsg != null ? `${fmtBRL(perMsg)} por mensagem` : '',
      validityLabel: p.validityDays ? `Validade ${p.validityDays} dias` : '',
      lastsLabel: hasRhythm ? durationLabel(qty / perDay) : '',
      savingsLabel: saving > 0 ? `${saving}% mais barato por mensagem` : '',
      highlighted: prods.length > 1 && i === Math.floor(prods.length / 2),
    };
  });

  const purchases = statement?.purchases ?? [];
  const toCompra = (c) => ({
    productId: String(c.productId ?? ''),
    packLabel: c.productName ?? 'Pacote',
    qtyLabel: c.creditQty ? `${fmtInt(c.creditQty)} mensagens` : '—',
    priceLabel: c.priceCents != null ? fmtBRL(c.priceCents) : '—',
    dateLabel: fmtDay(c.createdAt),
    agoLabel: formatRelativeDistance(c.createdAt),
  });
  const lastPaid = purchases.find((c) => c.status === 'fulfilled');
  const pending = purchases.find((c) => c.status === 'pending');

  // Lote perto de vencer: ainda com saldo, não expirado, vencendo em até 30 dias. O mais próximo manda.
  const expiring = (statement?.entries ?? [])
    .filter((e) => (e.remaining ?? 0) > 0 && e.expiresAt && new Date(e.expiresAt).getTime() > now)
    .map((e) => ({ e, days: Math.ceil((new Date(e.expiresAt).getTime() - now) / 86_400_000) }))
    .filter((x) => x.days <= EXPIRING_SOON_DAYS)
    .sort((a, b) => a.days - b.days)[0];

  return {
    productLabel: 'Mensagens WhatsApp',
    credits: {
      balance: saldo,
      balanceLabel: `${fmtInt(saldo)} ${plural(saldo, 'crédito', 'créditos')}`,
      loading: Boolean(meterLoading),
      level,
    },
    loss: {
      hasLosses: perdas > 0,
      totalLost: perdas,
      totalLostLabel: perdas > 0
        ? `${fmtInt(perdas)} ${plural(perdas, 'mensagem não enviada', 'mensagens não enviadas')} por falta de créditos`
        : '',
    },
    usage: {
      hasData: daily.length > 0 && totalConsumed > 0,
      days: daily.map((d) => ({ dateLabel: fmtDayShort(d.date), consumed: d.consumed ?? 0 })),
      maxConsumed: daily.reduce((m, d) => Math.max(m, d.consumed ?? 0), 0),
      totalConsumed,
      perWeekLabel: hasRhythm ? `cerca de ${fmtInt(Math.round(perDay * 7))} mensagens por semana` : '',
      runwayLabel,
    },
    packages,
    packagesLoading: Boolean(productsLoading),
    packagesEmpty: !productsLoading && !errorMessage && prods.length === 0,
    lastPurchase: lastPaid ? toCompra(lastPaid) : null,
    pendingPurchase: pending ? toCompra(pending) : null,
    expiringLabel: expiring
      ? `${fmtInt(expiring.e.remaining)} créditos expiram em ${expiring.days} ${plural(expiring.days, 'dia', 'dias')}`
      : '',
    errorMessage: errorMessage ?? null,
    arrivedFromAlert: Boolean(ref),
    canDisableIntegration: Boolean(integrationEnabled),
  };
}
